import React, { useState, useEffect } from 'react';
import { usersAPI, productsAPI } from '../../utils/api';
import { FaPlus, FaEdit, FaTrash, FaTag, FaTimes, FaSearch } from 'react-icons/fa';
import '../../styles/AdminUsers.css';

import { useTranslation } from 'react-i18next';


const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();

  const [showModal, setShowModal] = useState(false);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    restaurantName: '',
    address: { street: '', city: '', postalCode: '', country: 'Germany' },
    contactPerson: { firstName: '', lastName: '', phone: '' },
    deliveryRules: {
      days: []
    },
    isActive: true
  });

  const [discountData, setDiscountData] = useState({
    productId: '',
    discountType: 'percent',
    discountValue: ''
  });

  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [userSearchQuery, setUserSearchQuery] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [usersRes, productsRes] = await Promise.all([
        usersAPI.getUsers({ role: 'client' }),
        productsAPI.getProducts({ isActive: true })
      ]);
      setUsers(usersRes.data.data);
      
      // Фильтруем продукты, у которых есть _id
      const validProducts = productsRes.data.data.filter(p => p._id);
      setProducts(validProducts);
      
      // Проверяем, есть ли продукты без _id
      const invalidProducts = productsRes.data.data.filter(p => !p._id);
      if (invalidProducts.length > 0) {
        console.warn('Найдены продукты без _id:', invalidProducts);
      }
    } catch (error) {
      console.error('Ошибка загрузки данных:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        email: user.email,
        password: '',
        restaurantName: user.restaurantName,
        address: user.address || { street: '', city: '', postalCode: '', country: 'Germany' },
        contactPerson: user.contactPerson || { firstName: '', lastName: '', phone: '' },
        deliveryRules: user.deliveryRules || {
          days: []
        },
        isActive: user.isActive
      });
    } else {
      setEditingUser(null);
      setFormData({
        email: '',
        password: '',
        restaurantName: '',
        address: { street: '', city: '', postalCode: '', country: 'Germany' },
        contactPerson: { firstName: '', lastName: '', phone: '' },
        deliveryRules: {
          days: ['tuesday', 'thursday']
        },
        isActive: true
      });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    try {
      if (editingUser) {
        const updateData = { ...formData };
        if (!updateData.password) delete updateData.password; // Не отправляем пустой пароль
        await usersAPI.updateUser(editingUser._id, updateData);
        setMessage({ type: 'success', text: 'Пользователь обновлен' });
      } else {
        await usersAPI.createUser(formData);
        setMessage({ type: 'success', text: 'Пользователь создан' });
      }
      setShowModal(false);
      loadData();
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Ошибка сохранения пользователя' 
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Вы уверены, что хотите удалить этого пользователя?')) return;
    
    try {
      await usersAPI.deleteUser(id);
      setMessage({ type: 'success', text: 'Пользователь удален' });
      loadData();
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Ошибка удаления пользователя' 
      });
    }
  };

  const handleOpenDiscountModal = (user) => {
    setSelectedUser(user);
    setDiscountData({ productId: '', discountType: 'percent', discountValue: '' });
    setProductSearchQuery('');
    setShowDiscountModal(true);
  };

  const handleAddDiscount = async (e) => {
    e.preventDefault();
    
    // Валидация данных
    if (!discountData.productId) {
      setMessage({ type: 'error', text: 'Пожалуйста, выберите продукт' });
      return;
    }
    
    if (!discountData.discountValue || discountData.discountValue <= 0) {
      setMessage({ type: 'error', text: 'Пожалуйста, укажите значение скидки больше 0' });
      return;
    }
    
    try {
      const requestData = {
        productId: discountData.productId,
        discountType: discountData.discountType,
        discountValue: parseFloat(discountData.discountValue)
      };
      
      console.log('Отправка скидки:', requestData);
      console.log('Для пользователя:', selectedUser._id);
      
      await usersAPI.setDiscount(selectedUser._id, requestData);
      setMessage({ type: 'success', text: 'Скидка установлена' });
      
      // Обновляем общий список пользователей
      await loadData();
      
      // Обновляем selectedUser для модального окна
      const updatedUserResponse = await usersAPI.getUsers({ role: 'client' });
      const updatedUser = updatedUserResponse.data.data.find(u => u._id === selectedUser._id);
      if (updatedUser) {
        setSelectedUser(updatedUser);
      }
      
      // Очищаем форму
      setDiscountData({ productId: '', discountType: 'percent', discountValue: '' });
      setProductSearchQuery('');
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Ошибка установки скидки' 
      });
      console.error('Ошибка добавления скидки:', error);
    }
  };

  const handleRemoveDiscount = async (userId, productId) => {
    try {
      await usersAPI.removeDiscount(userId, productId);
      setMessage({ type: 'success', text: 'Скидка удалена' });
      
      // Обновляем общий список пользователей
      await loadData();
      
      // Если модальное окно скидок открыто, обновляем selectedUser
      if (selectedUser && selectedUser._id === userId) {
        // Получаем обновленные данные пользователя
        const updatedUserResponse = await usersAPI.getUsers({ role: 'client' });
        const updatedUser = updatedUserResponse.data.data.find(u => u._id === userId);
        if (updatedUser) {
          setSelectedUser(updatedUser);
        }
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Ошибка удаления скидки' });
      console.error('Ошибка удаления скидки:', error);
    }
  };

  const handleDayToggle = (day) => {
    setFormData(prev => ({
      ...prev,
      deliveryRules: {
        ...prev.deliveryRules,
        days: prev.deliveryRules.days.includes(day)
          ? prev.deliveryRules.days.filter(d => d !== day)
          : [...prev.deliveryRules.days, day]
      }
    }));
  };

  const daysOfWeek = [
    { value: 'monday', label: 'Пн' },
    { value: 'tuesday', label: 'Вт' },
    { value: 'wednesday', label: 'Ср' },
    { value: 'thursday', label: 'Чт' },
    { value: 'friday', label: 'Пт' },
    { value: 'saturday', label: 'Сб' },
    { value: 'sunday', label: 'Вс' }
  ];

  // Фильтрация клиентов по поисковому запросу
  const filteredUsers = users.filter(user => {
    if (!userSearchQuery) return true;
    const query = userSearchQuery.toLowerCase();
    return (
      user.restaurantName?.toLowerCase().includes(query) ||
      user.email?.toLowerCase().includes(query) ||
      user.address?.city?.toLowerCase().includes(query) ||
      user.contactPerson?.phone?.includes(query)
    );
  });

  return (
    <div className="admin-users-page">
      <div className="admin-users-header">
        <h1>Управление клиентами</h1>
        <button onClick={() => handleOpenModal()} className="btn-add">
          <FaPlus /> Создать клиента
        </button>
      </div>

      {message.text && (
        <div className={`message ${message.type}`}>{message.text}</div>
      )}

      <div className="users-controls">
        <div className="search-box">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Поиск по названию ресторана, email, городу или телефону..."
            value={userSearchQuery}
            onChange={(e) => setUserSearchQuery(e.target.value)}
            className="user-search-input"
          />
          {userSearchQuery && (
            <button 
              onClick={() => setUserSearchQuery('')} 
              className="clear-search-btn"
              title="Очистить поиск"
            >
              <FaTimes />
            </button>
          )}
        </div>
        {userSearchQuery && (
          <div className="search-results-info">
            Найдено клиентов: {filteredUsers.length} из {users.length}
          </div>
        )}
      </div>

      {loading ? (
        <div className="loading">Загрузка...</div>
      ) : filteredUsers.length === 0 ? (
        <div className="no-results">
          {userSearchQuery ? (
            <>
              <p>Клиенты не найдены по запросу "{userSearchQuery}"</p>
              <button onClick={() => setUserSearchQuery('')} className="btn-secondary">
                Очистить поиск
              </button>
            </>
          ) : (
            <p>Нет зарегистрированных клиентов</p>
          )}
        </div>
      ) : (
        <div className="users-grid">
          {filteredUsers.map(user => (
            <div key={user._id} className="user-card">
              <div className="user-card-header">
                <h3>{user.restaurantName}</h3>
                <span className={`status-badge ${user.isActive ? 'active' : 'inactive'}`}>
                  {user.isActive ? 'Активен' : 'Заблокирован'}
                </span>
              </div>

              <div className="user-info">
                <p><strong>Email:</strong> {user.email}</p>
                <p><strong>Город:</strong> {user.address?.city || '-'}</p>
                <p><strong>Телефон:</strong> {user.contactPerson?.phone || '-'}</p>
                <p><strong>Дней доставки:</strong> {user.deliveryRules?.days?.length || 0}</p>
              </div>

              {user.personalDiscounts && user.personalDiscounts.length > 0 && (
                <div className="user-discounts">
                  <strong>Скидки:</strong>
                  {user.personalDiscounts
                    .filter(discount => discount.product !== null)
                    .map(discount => (
                      <div key={discount.product._id} className="discount-tag">
                        {discount.product.name}: {' '}
                        {discount.discountType === 'percent' 
                          ? `${discount.discountValue}%` 
                          : `${discount.discountValue.toFixed(2)}€/шт`}
                        <button
                          onClick={() => handleRemoveDiscount(user._id, discount.product._id)}
                          className="remove-discount"
                        >
                          <FaTimes />
                        </button>
                      </div>
                    ))}
                </div>
              )}

              <div className="user-actions">
                <button onClick={() => handleOpenModal(user)} className="btn-icon">
                  <FaEdit /> Редактировать
                </button>
                <button onClick={() => handleOpenDiscountModal(user)} className="btn-icon">
                  <FaTag /> Скидки
                </button>
                <button onClick={() => handleDelete(user._id)} className="btn-icon delete">
                  <FaTrash />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingUser ? 'Редактировать клиента' : 'Новый клиент'}</h2>
              <button onClick={() => setShowModal(false)} className="modal-close">×</button>
            </div>

            <form onSubmit={handleSubmit} className="user-form">
              <div className="form-section">
                <h3>Основная информация</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Пароль {editingUser && '(оставьте пустым, чтобы не менять)'}</label>
                    <input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required={!editingUser}
                      minLength="6"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Название ресторана *</label>
                  <input
                    type="text"
                    value={formData.restaurantName}
                    onChange={(e) => setFormData({ ...formData, restaurantName: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="form-section">
                <h3>Адрес</h3>
                <div className="form-group">
                  <label>Улица</label>
                  <input
                    type="text"
                    value={formData.address.street}
                    onChange={(e) => setFormData({
                      ...formData,
                      address: { ...formData.address, street: e.target.value }
                    })}
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Город</label>
                    <input
                      type="text"
                      value={formData.address.city}
                      onChange={(e) => setFormData({
                        ...formData,
                        address: { ...formData.address, city: e.target.value }
                      })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Почтовый индекс</label>
                    <input
                      type="text"
                      value={formData.address.postalCode}
                      onChange={(e) => setFormData({
                        ...formData,
                        address: { ...formData.address, postalCode: e.target.value }
                      })}
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3>{t(`contactPerson`, 'Contact person')}</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>Имя</label>
                    <input
                      type="text"
                      value={formData.contactPerson.firstName}
                      onChange={(e) => setFormData({
                        ...formData,
                        contactPerson: { ...formData.contactPerson, firstName: e.target.value }
                      })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Фамилия</label>
                    <input
                      type="text"
                      value={formData.contactPerson.lastName}
                      onChange={(e) => setFormData({
                        ...formData,
                        contactPerson: { ...formData.contactPerson, lastName: e.target.value }
                      })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Телефон</label>
                    <input
                      type="tel"
                      value={formData.contactPerson.phone}
                      onChange={(e) => setFormData({
                        ...formData,
                        contactPerson: { ...formData.contactPerson, phone: e.target.value }
                      })}
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3>Правила доставки</h3>
                <div className="form-group">
                  <label>Дни доставки</label>
                  <div className="days-selector">
                    {daysOfWeek.map(day => (
                      <label key={day.value} className="day-checkbox">
                        <input
                          type="checkbox"
                          checked={formData.deliveryRules.days.includes(day.value)}
                          onChange={() => handleDayToggle(day.value)}
                        />
                        {day.label}
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                  Аккаунт активен
                </label>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-primary">
                  {editingUser ? 'Сохранить изменения' : 'Создать клиента'}
                </button>
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary">
                  Отмена
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDiscountModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowDiscountModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Управление скидками - {selectedUser.restaurantName}</h2>
              <button onClick={() => setShowDiscountModal(false)} className="modal-close">×</button>
            </div>

            <form onSubmit={handleAddDiscount} className="discount-form">
              <div className="form-group">
                <label>Поиск продукта</label>
                <input
                  type="text"
                  placeholder="Введите название продукта для поиска..."
                  value={productSearchQuery}
                  onChange={(e) => setProductSearchQuery(e.target.value)}
                  className="product-search-input"
                />
              </div>

              <div className="form-group">
                <label>Продукт</label>
                <select
                  value={discountData.productId}
                  onChange={(e) => setDiscountData({ ...discountData, productId: e.target.value })}
                  required
                  size="8"
                  style={{ minHeight: '200px' }}
                >
                  <option value="">-- Выберите продукт --</option>
                  {products
                    .filter(product => 
                      product.name.toLowerCase().includes(productSearchQuery.toLowerCase())
                    )
                    .map(product => (
                      <option key={product._id} value={product._id}>
                        {product.name} ({product.price.toFixed(2)} €)
                      </option>
                    ))}
                </select>
                {productSearchQuery && (
                  <small style={{ color: '#666', marginTop: '5px', display: 'block' }}>
                    Найдено: {products.filter(p => 
                      p.name.toLowerCase().includes(productSearchQuery.toLowerCase())
                    ).length} товаров
                  </small>
                )}
              </div>

              <div className="form-group">
                <label>Тип скидки</label>
                <select
                  value={discountData.discountType}
                  onChange={(e) => setDiscountData({ ...discountData, discountType: e.target.value })}
                  required
                >
                  <option value="percent">Процент (%)</option>
                  <option value="fixed">Фиксированная сумма (€)</option>
                  <option value="fixed_price">Цена со скидкой (€)</option>
                </select>
              </div>

              <div className="form-group">
                <label>
                  {discountData.discountType === 'percent' 
                    ? 'Процент скидки (%)' 
                    : discountData.discountType === 'fixed_price'
                    ? 'Цена со скидкой (€)'
                    : 'Скидка на единицу товара (€)'}
                </label>
                <input
                  type="number"
                  min="0"
                  max={discountData.discountType === 'percent' ? '100' : undefined}
                  step={discountData.discountType === 'percent' ? '1' : '0.01'}
                  value={discountData.discountValue}
                  onChange={(e) => setDiscountData({ ...discountData, discountValue: e.target.value })}
                  required
                  placeholder={
                    discountData.discountType === 'percent' 
                      ? 'Например: 15' 
                      : discountData.discountType === 'fixed_price'
                      ? 'Новая цена со скидкой'
                      : 'Например: 1.50'
                  }
                />
                {discountData.discountType === 'fixed' && (
                  <small style={{ color: '#666', fontSize: '0.85rem', display: 'block', marginTop: '5px' }}>
                    Скидка будет применена к каждой единице товара
                  </small>
                )}
                {discountData.discountType === 'fixed_price' && discountData.productId && (
                  <small style={{ color: '#666', fontSize: '0.85rem', display: 'block', marginTop: '5px' }}>
                    Введите конечную цену со скидкой. Старая цена будет показана автоматически.
                    {(() => {
                      const selectedProduct = products.find(p => p._id === discountData.productId);
                      if (selectedProduct && discountData.discountValue && discountData.discountValue < selectedProduct.price) {
                        const discountAmount = selectedProduct.price - discountData.discountValue;
                        const discountPercent = ((discountAmount / selectedProduct.price) * 100).toFixed(0);
                        return ` (Скидка: ${discountPercent}%, экономия ${discountAmount.toFixed(2)}€)`;
                      }
                      return '';
                    })()}
                  </small>
                )}
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-primary">
                  Установить скидку
                </button>
                <button type="button" onClick={() => setShowDiscountModal(false)} className="btn-secondary">
                  Закрыть
                </button>
              </div>
            </form>

            {selectedUser.personalDiscounts && selectedUser.personalDiscounts.length > 0 && (
              <div className="current-discounts">
                <h3>Текущие скидки:</h3>
                {selectedUser.personalDiscounts
                  .filter(discount => discount.product !== null)
                  .map(discount => (
                    <div key={discount.product._id} className="discount-item">
                      <span>{discount.product.name}</span>
                      <span className="discount-value">
                        {discount.discountType === 'percent' 
                          ? `${discount.discountValue}%` 
                          : discount.discountType === 'fixed_price'
                          ? `Цена: ${discount.discountValue.toFixed(2)} €` +
                            (discount.product.price ? ` (было: ${discount.product.price.toFixed(2)} €)` : '')
                          : `${discount.discountValue.toFixed(2)} € за шт.`}
                      </span>
                      <button
                        onClick={() => handleRemoveDiscount(selectedUser._id, discount.product._id)}
                        className="btn-icon delete"
                      >
                        <FaTimes />
                      </button>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;

